import axios, { AxiosInstance, AxiosError } from "axios";
import {
  ChatQuery,
  ChatResponse,
  ConversationHistory,
  Source,
  ApiError,
  MessageMetadata,
} from "../types";
import { secureTokenStorage, csrfManager } from "../utils/security";
import { environment } from "../environments";

interface MessagePayload {
  message: string;
  conversationId: string;
  userMetadata?: Record<string, unknown>;
}

// Enhanced API response interface for type safety
interface ApiResponse {
  answer?: string;
  response?: string;
  sources?: unknown[];
  conversation_id?: string;
  confidence?: number;
  query_type?: string;
  processing_time?: number;
  metadata?: unknown;
  [key: string]: unknown;
}

class ApiService {
  private client: AxiosInstance;
  private baseURL: string;

  constructor() {
    this.baseURL = `${environment.CHATBOT_BASE_URL}`;

    this.client = axios.create({
      baseURL: this.baseURL,
      timeout: 45000, // Increased timeout for complex MySQL queries
      headers: {
        "Content-Type": "application/json",
      },
      withCredentials: true, // Enable cookies for CSRF
    });

    // Initialize CSRF manager
    csrfManager.initialize();

    // Request interceptor for auth and CSRF
    this.client.interceptors.request.use(
      (config) => {
        // Add authentication token
        const token = secureTokenStorage.getToken();
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }

        // Add CSRF token for state-changing operations
        if (
          ["post", "put", "patch", "delete"].includes(
            config.method?.toLowerCase() || ""
          )
        ) {
          config.headers["X-CSRF-Token"] = csrfManager.getToken();
        }

        // Add request ID for tracking with fallback
        try {
          if (typeof crypto !== "undefined" && crypto.getRandomValues) {
            config.headers["X-Request-ID"] = crypto
              .getRandomValues(new Uint32Array(1))[0]
              .toString();
          } else {
            // Fallback for environments without crypto API
            config.headers["X-Request-ID"] =
              Date.now().toString() + Math.random().toString(36).substr(2, 9);
          }
        } catch (error) {
          // Fallback if crypto fails
          config.headers["X-Request-ID"] =
            Date.now().toString() + Math.random().toString(36).substr(2, 9);
        }

        return config;
      },
      (error) => Promise.reject(error)
    );

    // Response interceptor for error handling
    this.client.interceptors.response.use(
      (response) => response,
      (error: AxiosError) => {
        // Log errors in development only, avoid exposing sensitive information
        if (process.env.NODE_ENV === "development") {
          console.error("API Error:", error.response?.status, error.message);
        }
        return Promise.reject(this.transformError(error));
      }
    );
  }

  setBaseUrl(baseURL: string) {
    console.log("setBaseUrl baseURL: ", baseURL);
    
    this.client.defaults.baseURL = baseURL;
  }

  private transformError(error: AxiosError): ApiError {
    if (error.response) {
      return {
        detail: (error.response.data as any)?.detail || "An error occurred",
        status: error.response.status,
      };
    }

    if (error.code === "ECONNABORTED") {
      return {
        detail: "Request timeout - query may be too complex",
        status: 408,
      };
    }

    return { detail: "Network error", status: 0 };
  }

  // Legacy method for backward compatibility with improved type safety
  async sendMessage(
    messageOrPayload: string | MessagePayload,
    conversationIdOrApiUrl?: string,
    customHeaders?: Record<string, string>
  ): Promise<ChatResponse> {
    console.log("this.client: ", this.client);
    
    try {
      // Handle both legacy string format and new payload format
      let query: ChatQuery & {
        user_metadata?: Record<string, unknown>;
      };
      let apiUrl = this.baseURL;
      let headers: Record<string, string> = {};

      if (typeof messageOrPayload === "string") {
        // Legacy format: sendMessage(message, conversationId)
        query = {
          query: messageOrPayload,
          conversation_id:
            conversationIdOrApiUrl ||
            `conv_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
          stream: false,
        };
      } else {
        // New format: sendMessage(payload, apiUrl?, headers?)
        const payload = messageOrPayload;
        query = {
          query: payload.message,
          conversation_id: payload.conversationId,
          stream: false,
          user_metadata: payload.userMetadata,
        };

        if (conversationIdOrApiUrl) {
          apiUrl = conversationIdOrApiUrl;
        }

        if (customHeaders) {
          headers = customHeaders;
        }
      }

      // Validate inputs
      if (!query.query.trim()) {
        throw new Error("Message cannot be empty");
      }

      // Create a temporary client with custom baseURL if provided
      const useCustomClient = apiUrl !== this.baseURL;
      const client = useCustomClient
        ? axios.create({
            ...this.client.defaults,
            baseURL: apiUrl,
            headers: { ...this.client.defaults.headers, ...headers },
          })
        : this.client;

      // Add response interceptor to custom client to ensure proper error transformation
      if (useCustomClient) {
        client.interceptors.response.use(
          (response) => response,
          (error: AxiosError) => {
            return Promise.reject(this.transformError(error));
          }
        );
      }

      // Add custom headers to the request if using default client
      const requestConfig = apiUrl === this.baseURL ? { headers } : {};

      const response = await client.post<ApiResponse>(
        "/api/v1/chat/query",
        query,
        requestConfig
      );

      // Validate response structure
      if (!response.data) {
        throw new Error("Invalid response from server");
      }

      // Handle both new format (answer) and legacy format (response)
      const responseText = response.data.answer || response.data.response || "";

      if (!responseText) {
        throw new Error("No response received from server");
      }

      // Transform response to match our interface
      return {
        answer: responseText,
        response: responseText, // Backward compatibility
        sources: this.transformSources(response.data.sources || []),
        conversation_id: response.data.conversation_id || query.conversation_id,
        confidence: response.data.confidence,
        query_type: response.data.query_type,
        processing_time: response.data.processing_time,
        metadata: this.transformMetadata(response.data),
      };
    } catch (error) {
      // If already an ApiError (from interceptor), re-throw as-is
      if (
        error &&
        typeof error === "object" &&
        "detail" in error &&
        "status" in error &&
        typeof (error as any).status === "number"
      ) {
        throw error as ApiError;
      }

      // Re-throw with proper error transformation
      if (axios.isAxiosError(error)) {
        throw this.transformError(error);
      }

      // Handle non-Axios errors
      const apiError: ApiError = {
        detail:
          error instanceof Error ? error.message : "Unknown error occurred",
        status: 0,
      };
      throw apiError;
    }
  }

  private transformSources(apiSources: unknown[]): Source[] {
    if (!Array.isArray(apiSources)) return [];

    return apiSources.map((source) => {
      if (!source || typeof source !== "object") {
        return {
          type: "document" as const,
          id: "unknown",
          title: "Unknown Source",
          preview: undefined,
          confidence: undefined,
          url: null,
          relevance_score: undefined,
          snippet: undefined,
        };
      }

      const sourceObj = source as Record<string, unknown>;
      return {
        type: this.mapSourceType(
          (sourceObj.type as string) ||
            (sourceObj.source_type as string) ||
            "document"
        ),
        id:
          (sourceObj.id as string) ||
          (sourceObj.source_id as string) ||
          "unknown",
        title:
          (sourceObj.title as string) ||
          (sourceObj.name as string) ||
          "Database Query",
        preview:
          (sourceObj.preview as string) ||
          (typeof sourceObj.content === "string"
            ? sourceObj.content.substring(0, 100)
            : undefined),
        confidence:
          (sourceObj.confidence as number) ||
          (sourceObj.score as number) ||
          (sourceObj.relevance_score as number) ||
          undefined,
        url: (sourceObj.url as string) || null,
        relevance_score: sourceObj.relevance_score as number | undefined,
        snippet: sourceObj.snippet as string | undefined,
      };
    });
  }

  private transformMetadata(
    responseData: unknown
  ): MessageMetadata | undefined {
    if (!responseData || typeof responseData !== "object") return undefined;

    const data = responseData as Record<string, unknown>;
    const metadata = (data.metadata as Record<string, unknown>) || {};

    // Type-safe extraction with validation
    const extractString = (value: unknown): string | undefined =>
      typeof value === "string" ? value : undefined;

    const extractNumber = (value: unknown): number | undefined =>
      typeof value === "number" && !isNaN(value) ? value : undefined;

    return {
      intent: extractString(metadata.intent) || extractString(data.query_type),
      confidence:
        extractNumber(data.confidence) || extractNumber(metadata.confidence),
      query_type: extractString(data.query_type),
      processing_time:
        extractNumber(data.processing_time) ||
        extractNumber(metadata.processing_time),
      sql_query: extractString(metadata.sql_query),
      row_count: extractNumber(metadata.row_count),
      execution_time: extractNumber(metadata.execution_time),
      database_used: extractString(metadata.database_used),
    };
  }

  private mapSourceType(apiType: string): "mysql" | "document" | "faq" {
    const typeMap: { [key: string]: "mysql" | "document" | "faq" } = {
      mysql: "mysql",
      database: "mysql",
      sql: "mysql",
      db_query: "mysql",
      document: "document",
      pdf: "document",
      docx: "document",
      xlsx: "document",
      faq: "faq",
      knowledge_base: "faq",
    };

    return typeMap[apiType.toLowerCase()] || "document";
  }

  async getConversationHistory(
    conversationId: string
  ): Promise<ConversationHistory | null> {
    try {
      const response = await this.client.get<ConversationHistory>(
        `/api/v1/chat/conversations/${conversationId}`
      );
      return response.data;
    } catch (error) {
      console.error("Failed to fetch conversation history:", error);
      return null;
    }
  }

  async deleteConversation(conversationId: string): Promise<boolean> {
    try {
      await this.client.delete(`/api/v1/chat/conversations/${conversationId}`);
      return true;
    } catch (error) {
      console.error("Failed to delete conversation:", error);
      return false;
    }
  }

  async listConversations(limit = 10, offset = 0): Promise<any[]> {
    try {
      const response = await this.client.get("/api/v1/chat/conversations", {
        params: { limit, offset },
      });
      return response.data || [];
    } catch (error) {
      console.error("Failed to list conversations:", error);
      return [];
    }
  }

  async healthCheck(): Promise<boolean> {
    try {
      const response = await this.client.get("/api/v1/health/");
      return response.status === 200;
    } catch (error) {
      return false;
    }
  }

  async uploadDocument(file: File): Promise<any> {
    const formData = new FormData();
    formData.append("file", file);

    const response = await this.client.post(
      "/api/v1/documents/upload",
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      }
    );

    return response.data;
  }

  async getDocuments(): Promise<any[]> {
    try {
      const response = await this.client.get("/api/v1/documents");
      return response.data || [];
    } catch (error) {
      console.error("Failed to get documents:", error);
      return [];
    }
  }

  // New method to get database schema info (optional)
  async getDatabaseSchema(): Promise<any> {
    try {
      const response = await this.client.get("/api/v1/database/schema");
      return response.data;
    } catch (error) {
      console.error("Failed to get database schema:", error);
      return null;
    }
  }

  // New method to validate SQL queries (optional)
  async validateSQL(
    query: string
  ): Promise<{ valid: boolean; error?: string }> {
    try {
      const response = await this.client.post("/api/v1/database/validate", {
        query,
      });
      return response.data;
    } catch (error) {
      return { valid: false, error: "Validation service unavailable" };
    }
  }
}

export const apiService = new ApiService();
